import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import api from '../lib/api.js';
import { useSession } from '../SessionContext.jsx';

function MenuGroup({ group, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className={`group ${open ? 'open' : ''}`} ref={ref}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {group.name} ▾
      </button>
      {open && (
        <div className="dropdown">
          {group.items.map((item) => (
            <NavLink
              key={item.legacyLink || item.label}
              to={item.path || '/'}
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => { setOpen(false); onNavigate?.(item); }}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, app, reload, signOut } = useSession();
  const [stopping, setStopping] = useState(false);

  async function stopImpersonating() {
    setStopping(true);
    try {
      await api.del('/session/substitute');
      await api.post('/session/refresh');
      window.location.reload();
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className="app">
      <div className="topbar">
        <Link to="/Main" className="brand" style={{ color: '#fff', textDecoration: 'none' }}>
          {app?.company || 'FSWeb'}
        </Link>
        <span className="spacer" />
        <div className="whoami">
          <div>{user?.name} — {user?.company}</div>
          <div className="acct">
            {user?.windowsAccount || user?.userId}
            {user?.isAdministrator && ' · administrator'}
            {user?.isClient && ' · client'}
          </div>
        </div>
        <button className="signout" onClick={signOut}>Sign out</button>
      </div>

      {user?.clientScope === 'limited' && (
        <div className="scope-note">
          <span>
            Your account is limited to clients matching{' '}
            {user.clientPatterns.map((p, i) => (
              <span key={p}>
                {i > 0 && ', '}
                <code>{p}</code>
              </span>
            ))}
            . Everything below is filtered to those clients.
          </span>
        </div>
      )}

      {user?.clientScope === 'none' && (
        <div className="scope-note" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }}>
          Your account has no client access configured, so most screens will be empty. Ask an administrator to grant
          access in User Profiles.
        </div>
      )}

      {user?.impersonatedBy && (
        <div className="impersonating">
          <strong>Acting as {user.userId}</strong>
          <span>You are signed in as {user.impersonatedBy} and viewing FSWeb as another user.</span>
          <span className="spacer" />
          <button className="btn" onClick={stopImpersonating} disabled={stopping}>
            {stopping ? 'Returning...' : 'Return to my account'}
          </button>
        </div>
      )}

      <nav className="menubar">
        {(user?.menu || []).map((g) => (
          <MenuGroup key={g.name} group={g} onNavigate={reload} />
        ))}
      </nav>

      {children}

      <footer className="footer">
        <span>{app?.copyright}</span>
        <span className="spacer" />
        <span>Office {user?.office}</span>
        {app?.env !== 'production' && <span className="pill warn">{app?.env}</span>}
      </footer>
    </div>
  );
}
